// client/src/hooks/useContaPlans.js (VERSÃO 100% CORRIGIDA E COMPLETA)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';

const CONTA_PLANS_QUERY_KEY = ['contaPlans'];
const ACCOUNTS_FOR_PLAN_QUERY_KEY = ['accountsForPlan'];
const PLANS_STATS_QUERY_KEY = ['plansStats'];
const PLAN_COMPARISON_QUERY_KEY = ['planComparison'];

export const useContaPlans = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [CONTA_PLANS_QUERY_KEY, params],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/plans-contas', { 
          params: {
            ...params,
            includeStats: true // ✅ SOLICITAR ESTATÍSTICAS DO BACKEND
          }
        });
        
        if (response.data && response.data.success) {
          const plans = response.data.data || [];
          
          console.log('📊 Planos recebidos do backend:', plans.map(p => ({
            name: p.name,
            _id: p._id,
            usageStatistics: p.usageStatistics,
            price: p.price
          })));
          
          // ✅ CORREÇÃO CRÍTICA: Garantir que todos os planos tenham estatísticas válidas
          const processedPlans = plans.map(plan => {
            // Se não tem usageStatistics, criar estrutura básica
            if (!plan.usageStatistics && plan.price !== undefined) {
              plan.usageStatistics = {
                activeSubscriptions: 0,
                trialAccounts: 0,
                totalAccounts: 0,
                totalActiveUsers: 0,
                totalRevenue: plan.price, // Receita potencial
                totalRevenueInReais: plan.price / 100 // Converter para reais
              };
            }
            
            // ✅ CORREÇÃO: Garantir estrutura consistente das features
            if (plan.features) {
              const processedFeatures = { ...plan.features };
              
              Object.keys(processedFeatures).forEach(key => {
                if (key.startsWith('limite') && processedFeatures[key]) {
                  if (typeof processedFeatures[key] === 'object') {
                    processedFeatures[key] = {
                      value: processedFeatures[key].value || 0,
                      isUnlimited: !!processedFeatures[key].isUnlimited
                    };
                  } else if (typeof processedFeatures[key] === 'number') {
                    processedFeatures[key] = {
                      value: processedFeatures[key],
                      isUnlimited: false
                    };
                  }
                }
              });
              
              plan.features = processedFeatures;
            }
            
            return plan;
          });
          
          return processedPlans;
        } else {
          throw new Error('Estrutura de dados inválida da API');
        }
      } catch (error) {
        console.error('❌ Erro ao buscar planos:', error);
        throw error;
      }
    },
    ...options 
  });
};

export const useContaPlanById = (id, options = {}) => {
  return useQuery({
    queryKey: [CONTA_PLANS_QUERY_KEY, id],
    queryFn: async () => {
      if (!id) return null;
      
      try {
        const response = await apiClient.get(`/plans-contas/${id}`);
        
        if (response.data && response.data.success) {
          const plan = response.data.data;
          
          console.log('📥 Plano específico carregado:', {
            name: plan.name,
            _id: plan._id,
            usageStatistics: plan.usageStatistics,
            price: plan.price
          });

          // ✅ CORREÇÃO CRÍTICA: Se não tem usageStatistics, criar estrutura básica
          if (!plan.usageStatistics && plan.price !== undefined) {
            plan.usageStatistics = {
              activeSubscriptions: 0,
              trialAccounts: 0,
              totalAccounts: 0,
              totalActiveUsers: 0,
              totalRevenue: plan.price, // Receita potencial
              totalRevenueInReais: plan.price / 100 // Converter para reais
            };
            
            console.log('🔄 Estatísticas criadas para o plano:', {
              name: plan.name,
              price: plan.price,
              totalRevenueInReais: plan.usageStatistics.totalRevenueInReais
            });
          }

          // ✅ CORREÇÃO: Garantir estrutura consistente das features
          if (plan.features) {
            const processedFeatures = { ...plan.features };
            
            Object.keys(processedFeatures).forEach(key => {
              if (key.startsWith('limite') && processedFeatures[key]) {
                if (typeof processedFeatures[key] === 'object') {
                  processedFeatures[key] = {
                    value: processedFeatures[key].value || 0,
                    isUnlimited: !!processedFeatures[key].isUnlimited
                  };
                } else if (typeof processedFeatures[key] === 'number') {
                  processedFeatures[key] = {
                    value: processedFeatures[key],
                    isUnlimited: false
                  };
                }
              }
            });
            
            plan.features = processedFeatures;
          }
          
          return plan;
        } else {
          throw new Error(response.data?.message || 'Erro ao carregar plano');
        }
      } catch (error) {
        console.error(`❌ Erro ao buscar plano ${id}:`, error);
        throw error;
      }
    },
    enabled: !!id,
    ...options
  });
};

export const useAccountsForPlan = (planId, params = {}, options = {}) => {
  return useQuery({
    queryKey: [ACCOUNTS_FOR_PLAN_QUERY_KEY, planId, params],
    queryFn: async () => {
      if (!planId) {
        return { 
          data: [], 
          statistics: {
            totalAccounts: 0,
            activeSubscriptions: 0,
            trialAccounts: 0,
            totalRevenueInReais: 0
          }, 
          count: 0, 
          total: 0 
        };
      }
      
      try {
        const response = await apiClient.get(`/plans-contas/${planId}/accounts`, { params });
        
        if (response.data && response.data.success) {
          console.log('📊 Dados das contas recebidos:', {
            total: response.data.total,
            statistics: response.data.statistics,
            accountsCount: response.data.data?.length
          });

          // ✅ CORREÇÃO: Se não tem estatísticas, criar estrutura básica
          const statistics = response.data.statistics || {
            totalAccounts: response.data.total || 0,
            activeSubscriptions: 0,
            trialAccounts: 0,
            totalRevenueInReais: 0
          };

          // ✅ CORREÇÃO CRÍTICA: Processar dados das contas para estrutura consistente
          const processedData = {
            ...response.data,
            statistics,
            data: (response.data.data || []).map(account => ({
              // ✅ GARANTIR CAMPOS OBRIGATÓRIOS
              _id: account._id,
              id: account._id || account.id,
              
              // ✅ CORREÇÃO: Garantir name sempre preenchido
              name: account.name || account.nomeFantasia || 'Conta Sem Nome',
              nomeFantasia: account.nomeFantasia || null,
              
              // ✅ DADOS DE CONTATO
              email: account.email || null,
              telefone: account.telefone || null,
              status: account.status || 'inactive',
              
              // ✅ DADOS DE FRANQUIA
              franquia: account.franquia || null,
              
              // ✅ DADOS DE ASSINATURA
              subscription: account.subscription ? {
                _id: account.subscription._id,
                status: account.subscription.status,
                currentPeriodEnd: account.subscription.currentPeriodEnd,
                trialEnd: account.subscription.trialEnd,
                pagarmeSubscriptionId: account.subscription.pagarmeSubscriptionId || 
                                      account.subscription.gatewaySubscriptionId,
                id: account.subscription._id
              } : null,
              
              // ✅ DATAS
              trialExpiresAt: account.trialExpiresAt,
              createdAt: account.createdAt,
              updatedAt: account.updatedAt
            }))
          };

          console.log('✅ Dados processados das contas:', {
            totalAccounts: processedData.total,
            statistics: processedData.statistics,
            accounts: processedData.data.map(acc => ({
              id: acc.id,
              name: acc.name,
              email: acc.email,
              status: acc.status
            }))
          });

          return processedData;
        } else {
          throw new Error(response.data?.message || 'Erro ao carregar contas');
        }
      } catch (error) {
        console.error(`❌ Erro ao buscar contas do plano ${planId}:`, error);
        throw error;
      }
    },
    enabled: !!planId,
    ...options
  });
};

export const usePlansStats = (options = {}) => {
  return useQuery({
    queryKey: [PLANS_STATS_QUERY_KEY],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/plans-contas/stats/overview');
        
        if (response.data && response.data.success) {
          console.log('📈 Estatísticas recebidas:', {
            totalPlans: response.data.data?.plans?.total,
            totalSubscribers: response.data.data?.usage?.totalSubscribers,
            totalRevenue: response.data.data?.revenue?.totalRevenueInReais
          });
          
          // ✅ CORREÇÃO: Garantir estrutura válida para estatísticas
          const statsData = response.data.data || {};
          
          // Se não tem dados de receita, calcular a partir dos planos
          if (!statsData.revenue && statsData.plans) {
            const plansResponse = await apiClient.get('/plans-contas?includeStats=true');
            if (plansResponse.data.success) {
              const plans = plansResponse.data.data || [];
              let totalRevenueInReais = 0;
              
              plans.forEach(plan => {
                if (plan.usageStatistics?.totalRevenueInReais) {
                  totalRevenueInReais += plan.usageStatistics.totalRevenueInReais;
                } else if (plan.price) {
                  totalRevenueInReais += plan.price / 100;
                }
              });
              
              statsData.revenue = {
                totalRevenueInReais: totalRevenueInReais,
                monthlyRecurringRevenue: totalRevenueInReais
              };
            }
          }
          
          return statsData;
        } else {
          throw new Error(response.data?.message || 'Erro ao carregar estatísticas');
        }
      } catch (error) {
        console.error('❌ Erro ao buscar estatísticas:', error);
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000,
    ...options
  });
};

export const usePlanComparison = (planIds = [], params = {}, options = {}) => {
  return useQuery({
    queryKey: [PLAN_COMPARISON_QUERY_KEY, planIds, params],
    queryFn: async () => {
      if (!planIds || planIds.length === 0) return null;
      
      try {
        const idsString = planIds.join(',');
        const response = await apiClient.get(`/plans-contas/compare/${idsString}`, { params });
        
        if (response.data && response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data?.message || 'Erro ao comparar planos');
        }
      } catch (error) {
        console.error('❌ Erro ao comparar planos:', error);
        throw error;
      }
    },
    enabled: !!planIds && planIds.length > 0,
    staleTime: 15 * 60 * 1000,
    ...options
  });
};

export const useDefaultTrialPlan = (options = {}) => {
  return useQuery({
    queryKey: [CONTA_PLANS_QUERY_KEY, 'defaultTrial'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/plans-contas/trial/default');
        
        if (response.data && response.data.success) {
          const plan = response.data.data;
          
          // ✅ CORREÇÃO: Garantir estatísticas para o plano trial
          if (!plan.usageStatistics) {
            plan.usageStatistics = {
              activeSubscriptions: 0,
              trialAccounts: 0,
              totalAccounts: 0,
              totalActiveUsers: 0,
              totalRevenue: 0,
              totalRevenueInReais: 0
            };
          }
          
          return plan;
        } else {
          throw new Error(response.data?.message || 'Erro ao carregar plano trial padrão');
        }
      } catch (error) {
        console.error('❌ Erro ao buscar plano trial padrão:', error);
        throw error;
      }
    },
    staleTime: 30 * 60 * 1000,
    ...options
  });
};

// ✅ HOOK DE MUTATIONS CORRIGIDO
export const useMutateContaPlans = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const createMutation = useMutation({
    mutationFn: async (planData) => {
      const response = await apiClient.post('/plans-contas', planData);
      return response.data;
    },
    onSuccess: (data) => {
      // ✅ INVALIDAR TODOS OS CACHES RELACIONADOS
      queryClient.invalidateQueries({ queryKey: CONTA_PLANS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PLANS_STATS_QUERY_KEY });
      setAlert(data.message || 'Plano criado com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           'Erro ao criar plano. Tente novamente.';
      setAlert(errorMessage, 'danger');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, planData }) => {
      const response = await apiClient.put(`/plans-contas/${id}`, planData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // ✅ INVALIDAR TODOS OS CACHES RELACIONADOS
      queryClient.invalidateQueries({ queryKey: CONTA_PLANS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [CONTA_PLANS_QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: PLANS_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_FOR_PLAN_QUERY_KEY, variables.id] });
      setAlert(data.message || 'Plano atualizado com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           'Erro ao atualizar plano. Tente novamente.';
      setAlert(errorMessage, 'danger');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      if (!window.confirm('Tem certeza que deseja desativar este plano? Esta ação não pode ser desfeita.')) {
        throw new Error('Ação cancelada pelo usuário');
      }
      
      const response = await apiClient.delete(`/plans-contas/${id}`);
      return response.data;
    },
    onSuccess: (data) => {
      // ✅ INVALIDAR TODOS OS CACHES RELACIONADOS
      queryClient.invalidateQueries({ queryKey: CONTA_PLANS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PLANS_STATS_QUERY_KEY });
      setAlert(data.message || 'Plano desativado com sucesso.', 'success');
    },
    onError: (error) => {
      if (error.message !== 'Ação cancelada pelo usuário') {
        const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             'Erro ao desativar plano. Tente novamente.';
        setAlert(errorMessage, 'danger');
      }
    },
  });

  return { 
    // Mutations originais
    createMutation,
    updateMutation,
    deleteMutation,
    
    // Funções de conveniência
    createPlan: createMutation.mutate,
    updatePlan: updateMutation.mutate,
    deletePlan: deleteMutation.mutate,
    
    // Estados de loading
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Erros
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    
    // Utilitários adicionais
    reset: () => {
      createMutation.reset();
      updateMutation.reset();
      deleteMutation.reset();
    }
  };
};

// ✅ HOOK PARA FEATURES E UTILITÁRIOS DE PLANOS (COMPLETO)
export const usePlanFeatures = () => {
  const getDefaultFeatures = () => ({
    // ✅ FEATURES PRINCIPAIS
    hasAgenda: true,
    hasRecursos: false,
    hasItensVenda: false,
    hasPacotes: false,
    hasTiposContrato: false,
    hasContratos: false,
    hasTemplates: false,
    hasTemplateAnamnese: false,
    hasTemplateCertificado: false,
    hasTemplateContrato: false,
    hasTemplateRecibo: false,
    hasTransportadores: false,
    hasGaleria: false,
    hasFornecedores: false,
    hasServicos: true,

    // ✅ NOVAS FEATURES
    hasCarrinho: true,
    hasCategorias: true,
    hasComunicacao: false,
    hasDocumentosGerados: false,
    hasHistoricoComunicacao: false,
    hasMetodosPagamento: true,
    hasOfertas: false,
    hasPacotesClientes: false,
    hasPaymentGateway: false,
    hasPagamentos: true,
    hasPedidos: true,
    hasUnidades: true,
    hasVendas: true,
    hasCursos: false,

    // ✅ CORREÇÃO: hasFidelidade/hasListaEspera/hasNotaFiscal existem no
    // model (ContasPlan.js, set 2026) desde que essas três features viraram
    // plan-gated, mas nunca tinham default aqui nem controle no formulário
    // — toda conta com essas features habilitadas direto no banco (fora
    // deste formulário) perdia o toggle ao reabrir o plano pra editar.
    hasFidelidade: false,
    hasListaEspera: false,
    hasNotaFiscal: false,

    // ✅ FEATURES AVANÇADAS
    hasRelatoriosAvancados: false,
    hasIntegracaoWhatsApp: false,
    hasIntegracaoEmail: false,
    // ✅ CORREÇÃO: existiam no model, nunca tinham default nem controle aqui
    hasIntegracaoSMS: false,
    hasCanalCentralizado: false,
    hasMultiplasMoedas: false,
    hasBackupAutomatico: false,
    hasAPIAccess: false,
    hasWhiteLabel: false,
    hasSuportePrioritario: false,
    hasHomePersonalizada: false,
    // ✅ CORREÇÃO: existiam no model (ContasPlan.js) e no formulário
    // (PlansContasFormPage.js), nunca tinham default aqui — plano novo
    // sempre criava sem esses campos no payload até o admin tocar no toggle.
    hasGeracaoIA: false,
    hasGoogleNegocio: false,
    hasLoginGoogle: false,

    // ✅ LIMITES PRINCIPAIS
    limiteUsuarios: { isUnlimited: false, value: 1 },
    limiteRecursos: { isUnlimited: false, value: 1 },
    limiteClientes: { isUnlimited: false, value: 50 },
    limiteFornecedores: { isUnlimited: false, value: 5 },
    limiteServicos: { isUnlimited: false, value: 10 },
    limiteItensVenda: { isUnlimited: false, value: 0 },
    limitePacotes: { isUnlimited: false, value: 0 },
    limiteContratos: { isUnlimited: false, value: 10 },
    limiteTiposContrato: { isUnlimited: false, value: 1 },
    limiteTemplates: { isUnlimited: false, value: 2 },
    limiteTransportadores: { isUnlimited: false, value: 0 },
    limiteUnidades: { isUnlimited: false, value: 1 },

    // ✅ LIMITES DE TEMPLATES
    limiteTemplateAnamnese: { isUnlimited: false, value: 2 },
    limiteTemplateCertificado: { isUnlimited: false, value: 2 },
    limiteTemplateContrato: { isUnlimited: false, value: 2 },
    limiteTemplateMensagem: { isUnlimited: false, value: 2 },
    limiteTemplateRecibo: { isUnlimited: false, value: 2 },

    // ✅ NOVOS LIMITES
    limiteOfertas: { isUnlimited: false, value: 0 },
    limitePacotesClientes: { isUnlimited: false, value: 0 },
    limiteCategorias: { isUnlimited: false, value: 10 },
    limiteMetodosPagamento: { isUnlimited: false, value: 3 },
    limiteCursos: { isUnlimited: false, value: 0 },

    // ✅ LIMITES MENSÁIS
    limitePedidosMensais: { isUnlimited: false, value: 100 },
    limiteVendasMensais: { isUnlimited: false, value: 100 },
    limiteAgendamentosMensais: { isUnlimited: false, value: 200 },
    limiteComunicacoesMensais: { isUnlimited: false, value: 50 },

    // ✅ CONFIGURAÇÕES DE PERFORMANCE
    maxStorageGB: 1,
    maxFileSizeMB: 10,
    maxAgendamentosSimultaneos: 1,
    retentionPeriodDays: 90
  });

  const validatePlanData = (planData) => {
    const errors = [];

    if (!planData.name?.trim()) {
      errors.push('Nome do plano é obrigatório');
    }

    if (planData.price === undefined || planData.price === null || planData.price === '') {
      errors.push('Preço é obrigatório');
    }

    if (planData.price && parseFloat(planData.price.replace(',', '.')) < 0) {
      errors.push('Preço não pode ser negativo');
    }

    if (planData.trialPeriodDays && (planData.trialPeriodDays < 0 || planData.trialPeriodDays > 365)) {
      errors.push('Período de trial deve estar entre 0 e 365 dias');
    }

    // ✅ VALIDAÇÃO DE FEATURES
    if (planData.features) {
      const requiredFeatures = ['hasAgenda', 'hasServicos'];
      requiredFeatures.forEach(feature => {
        if (planData.features[feature] === undefined) {
          errors.push(`Feature ${feature} é obrigatória`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const formatPrice = (priceInCents) => {
    if (typeof priceInCents !== 'number') return 'N/A';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(priceInCents / 100);
  };

  const getUpgradeInfo = (currentPlan, targetPlan) => {
    if (!currentPlan || !targetPlan) return null;

    const currentFeatures = currentPlan.features || {};
    const targetFeatures = targetPlan.features || {};

    const newFeatures = Object.keys(targetFeatures)
      .filter(key => key.startsWith('has') && 
                    targetFeatures[key] === true && 
                    currentFeatures[key] === false);

    const increasedLimits = Object.keys(targetFeatures)
      .filter(key => key.startsWith('limite'))
      .filter(key => {
        const currentLimit = typeof currentFeatures[key] === 'object' ? 
          currentFeatures[key]?.value : currentFeatures[key] || 0;
        const targetLimit = typeof targetFeatures[key] === 'object' ? 
          targetFeatures[key]?.value : targetFeatures[key] || 0;
        return targetLimit > currentLimit;
      });

    const priceDifference = (targetPlan.price || 0) - (currentPlan.price || 0);

    return {
      priceDifference,
      priceDifferenceFormatted: formatPrice(Math.abs(priceDifference)),
      newFeatures: newFeatures.map(f => f.replace('has', '').replace(/([A-Z])/g, ' $1').trim()),
      increasedLimits: increasedLimits.map(l => l.replace('limite', '').replace(/([A-Z])/g, ' $1').trim()),
      isUpgrade: priceDifference > 0,
      isDowngrade: priceDifference < 0,
      isSamePrice: priceDifference === 0,
      hasChanges: newFeatures.length > 0 || increasedLimits.length > 0 || priceDifference !== 0
    };
  };

  // ✅ Calcular valor total baseado nos limites
  const calculatePlanValue = (plan) => {
    if (!plan || !plan.features) return 0;
    
    let baseValue = plan.price || 0;
    const features = plan.features;
    
    // Adicionar valor por features habilitadas
    Object.keys(features).forEach(key => {
      if (key.startsWith('has') && features[key] === true) {
        baseValue += 500; // Valor base por feature
      }
    });
    
    // Adicionar valor por limites aumentados
    Object.keys(features).forEach(key => {
      if (key.startsWith('limite') && features[key]) {
        const limit = features[key];
        const value = typeof limit === 'object' ? limit.value : limit;
        if (value > 0 && value < 1000) {
          baseValue += value * 10; // R$ 0,10 por unidade de limite
        }
      }
    });
    
    return baseValue;
  };

  // ✅ Verificar se plano é recomendado para um negócio
  const getBusinessRecommendation = (plan, businessType) => {
    if (!plan || !businessType) return null;
    
    const features = plan.features || {};
    const recommendations = {
      small: {
        required: ['hasAgenda', 'hasServicos'],
        recommended: ['hasCarrinho', 'hasVendas'],
        maxPrice: 5000
      },
      medium: {
        required: ['hasAgenda', 'hasServicos', 'hasVendas', 'hasRelatoriosAvancados'],
        recommended: ['hasComunicacao', 'hasIntegracaoWhatsApp'],
        maxPrice: 20000
      },
      large: {
        required: ['hasAgenda', 'hasServicos', 'hasVendas', 'hasRelatoriosAvancados', 'hasAPIAccess'],
        recommended: ['hasWhiteLabel', 'hasSuportePrioritario', 'hasBackupAutomatico'],
        maxPrice: 50000
      }
    };
    
    const config = recommendations[businessType];
    if (!config) return null;
    
    const missingRequired = config.required.filter(feature => !features[feature]);
    const hasRecommended = config.recommended.filter(feature => features[feature]);
    
    return {
      isSuitable: missingRequired.length === 0 && (plan.price || 0) <= config.maxPrice,
      missingRequired,
      hasRecommended: hasRecommended.length,
      totalRecommended: config.recommended.length,
      score: Math.round(((config.required.length - missingRequired.length) / config.required.length) * 100)
    };
  };

  return {
    // Funções principais
    getDefaultFeatures,
    validatePlanData,
    formatPrice,
    getUpgradeInfo,
    
    // Novas funções utilitárias
    calculatePlanValue,
    getBusinessRecommendation,
    
    // Utilitários adicionais
    isFreePlan: (plan) => !plan?.price || plan.price === 0,
    hasTrial: (plan) => plan?.trialPeriodDays > 0,
    isActive: (plan) => plan?.isActive !== false,
    
    // Constantes úteis
    FEATURE_CATEGORIES: {
      basic: ['hasAgenda', 'hasServicos', 'hasCarrinho', 'hasVendas'],
      advanced: ['hasRelatoriosAvancados', 'hasIntegracaoWhatsApp', 'hasAPIAccess'],
      enterprise: ['hasWhiteLabel', 'hasSuportePrioritario', 'hasBackupAutomatico']
    },
    
    LIMIT_CATEGORIES: {
      users: ['limiteUsuarios'],
      storage: ['maxStorageGB', 'maxFileSizeMB'],
      business: ['limiteClientes', 'limiteServicos', 'limiteVendasMensais']
    }
  };
};

// ✅ HOOK ADICIONAL: Para estatísticas em tempo real
export const useRealTimePlanStats = (planId, options = {}) => {
  return useQuery({
    queryKey: ['realTimePlanStats', planId],
    queryFn: async () => {
      if (!planId) return null;
      
      try {
        const [planResponse, accountsResponse] = await Promise.all([
          apiClient.get(`/plans-contas/${planId}`),
          apiClient.get(`/plans-contas/${planId}/accounts?limit=5`)
        ]);
        
        if (planResponse.data.success && accountsResponse.data.success) {
          const plan = planResponse.data.data;
          
          // ✅ CORREÇÃO: Garantir estatísticas válidas
          if (!plan.usageStatistics && plan.price !== undefined) {
            plan.usageStatistics = {
              activeSubscriptions: 0,
              trialAccounts: 0,
              totalAccounts: 0,
              totalActiveUsers: 0,
              totalRevenue: plan.price,
              totalRevenueInReais: plan.price / 100
            };
          }
          
          return {
            plan: plan,
            recentAccounts: accountsResponse.data.data,
            statistics: accountsResponse.data.statistics || plan.usageStatistics
          };
        } else {
          throw new Error('Erro ao carregar estatísticas em tempo real');
        }
      } catch (error) {
        console.error('❌ Erro ao buscar estatísticas em tempo real:', error);
        throw error;
      }
    },
    enabled: !!planId,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    ...options
  });
};

